import { useRouteError } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { reportError } from '@/lib/errorReporter';

const isChunkLoadError = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes('<!DOCTYPE') ||
    msg.includes('is not valid JSON') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    // Firefox phrasing for the same stale-chunk-after-deploy failure.
    msg.includes('error loading dynamically imported module') ||
    // Safari phrasing.
    msg.includes('Importing a module script failed') ||
    // A stale index.html requesting an export that no longer exists in the new
    // chunk after a deploy — same root cause, same SW-nuke + reload recovery.
    msg.includes('Importing binding name') ||
    msg.includes('Unable to preload CSS') ||
    msg.includes('Unexpected token') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading chunk') ||
    msg.includes('Minified React error #423') ||
    msg.includes('Minified React error #418') ||
    msg.includes('Minified React error #425') ||
    msg.includes('hydrat')
  );
};

export default function RootErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation('common');

  let reloadCount = 0;
  try {
    reloadCount = isChunkLoadError(error)
      ? parseInt(sessionStorage.getItem('chunk-reload-count') || '0')
      : 0;
  } catch { /* sessionStorage unavailable (private browsing, etc.) */ }
  const shouldAutoReload = isChunkLoadError(error) && reloadCount < 2;

  useEffect(() => {
    // Skip reporting when we're about to auto-reload — the user recovers
    // silently and the digest stays focused on errors that actually broke
    // something. Only report when the fallback UI is what the user sees:
    // a non-chunk error, or a chunk error past the retry budget.
    if (error && !shouldAutoReload) {
      reportError(error, {
        component: 'RootErrorBoundary',
        isChunkLoad: isChunkLoadError(error),
      });
    }
  }, [error, shouldAutoReload]);

  useEffect(() => {
    if (shouldAutoReload) {
      // A chunk-load failure after a deploy is usually a stale or poisoned
      // service-worker cache: it serves an old or HTML-for-asset response, so a
      // plain reload just re-serves the bad bytes (which is why a simple reload
      // didn't recover these users). Nuke the SW + all caches first, THEN reload
      // so the chunks come fresh from the network. The reload-count guard caps
      // this at 2 attempts to avoid a loop.
      try { sessionStorage.setItem('chunk-reload-count', String(reloadCount + 1)); } catch { /* ignore */ }
      void (async () => {
        try {
          if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(
              keys.filter((k) => k.startsWith('cctvplanner-')).map((k) => caches.delete(k)),
            );
          }
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
        } catch { /* best effort — reload regardless */ }
        window.location.reload();
      })();
    } else if (isChunkLoadError(error)) {
      // Max retries reached — clear counter so future navigations can retry
      try { sessionStorage.removeItem('chunk-reload-count'); } catch { /* ignore */ }
    }
  }, [error, shouldAutoReload, reloadCount]);

  if (shouldAutoReload) {
    // Show nothing while reloading
    return null;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-graphite-900 flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <span className="font-mono text-[10px] font-semibold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-3 block">§ ERROR</span>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{t('error_something_went_wrong')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm">
          {t('error_unexpected')}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-neutral-900 dark:bg-white hover:bg-neutral-800 dark:hover:bg-neutral-200 text-white dark:text-neutral-900 text-xs font-mono font-semibold uppercase tracking-wide transition-colors"
        >
          {t('error_refresh_page')} &rarr;
        </button>
      </div>
    </div>
  );
}
